import { useState, useEffect, useCallback } from "react";
import { Pencil, Trash2, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// group_name format: /^[a-z0-9][a-z0-9 _-]{0,62}[a-z0-9]$/i
// Also allow single char that matches [a-z0-9]
const GROUP_NAME_PATTERN = /^[a-z0-9]([a-z0-9 _-]{0,62}[a-z0-9])?$/i;

function isValidGroupName(name) {
  if (!name) return true; // empty = no group (allowed)
  return GROUP_NAME_PATTERN.test(name);
}

function formatConsentDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// SenderModeCard — toggle between donotreply (ACS) and Outlook (Graph)
// ---------------------------------------------------------------------------

function SenderModeCard({ refreshKey, onChanged }) {
  const [status, setStatus] = useState(null); // null = loading; object = loaded
  const [loadError, setLoadError] = useState(false);
  const [pending, setPending] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await api.get("/email/oauth/status");
      setStatus(res.data);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, refreshKey]);

  async function applyMode(mode) {
    setPending(true);
    try {
      const res = await api.patch("/email/oauth/sender-mode", { mode });
      setStatus(res.data);
      toast.success(
        mode === "outlook"
          ? "Sending mode set to Outlook."
          : "Sending mode set to donotreply@elementarai.com.",
      );
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(
        getErrorMessage(err, "Failed to update sending mode. Please try again."),
      );
    } finally {
      setPending(false);
      setConfirmDialogOpen(false);
    }
  }

  function handleSelect(targetMode) {
    if (!status) return;
    if (status.sender_mode === targetMode) return;
    if (targetMode === "outlook" && status.status !== "connected") {
      toast.error("Connect your Outlook below first.");
      return;
    }
    if (targetMode === "donotreply" && status.sender_mode === "outlook") {
      // confirmation: switching away from Outlook
      setConfirmDialogOpen(true);
      return;
    }
    applyMode(targetMode);
  }

  if (loadError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sending mode</CardTitle>
          <CardDescription>How tenant emails are sent.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600">Unable to load sending mode.</p>
        </CardContent>
      </Card>
    );
  }
  if (status === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sending mode</CardTitle>
          <CardDescription>How tenant emails are sent.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  const currentMode = status.sender_mode || "donotreply";
  const canPickOutlook = status.status === "connected";

  const optionBase =
    "flex-1 px-4 py-3 rounded-md border text-sm text-left transition-colors";
  const optionSelected = "border-2";
  const optionUnselected = "border-gray-200 hover:border-gray-300";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Sending mode</CardTitle>
          <CardDescription>
            Choose how documents and digests are delivered for this tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              disabled={pending}
              onClick={() => handleSelect("donotreply")}
              className={`${optionBase} ${
                currentMode === "donotreply" ? optionSelected : optionUnselected
              }`}
              style={
                currentMode === "donotreply"
                  ? { borderColor: BRAND.bg, backgroundColor: BRAND.soft }
                  : {}
              }
              data-testid="sender-mode-donotreply"
            >
              <div className="font-medium">donotreply@elementarai.com</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Default. Recipients see a no-reply Elementar address. No setup required.
              </div>
            </button>
            <button
              type="button"
              disabled={pending || !canPickOutlook}
              onClick={() => handleSelect("outlook")}
              title={
                canPickOutlook
                  ? undefined
                  : "Connect your Outlook below first"
              }
              className={`${optionBase} ${
                currentMode === "outlook" ? optionSelected : optionUnselected
              } ${!canPickOutlook ? "opacity-60 cursor-not-allowed" : ""}`}
              style={
                currentMode === "outlook"
                  ? { borderColor: BRAND.bg, backgroundColor: BRAND.soft }
                  : {}
              }
              data-testid="sender-mode-outlook"
            >
              <div className="font-medium">Connected Outlook mailbox</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {canPickOutlook
                  ? `Sends from ${status.connected_mailbox}.`
                  : "Connect Outlook below to enable."}
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={confirmDialogOpen}
        onOpenChange={(o) => !o && setConfirmDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to donotreply sending?</AlertDialogTitle>
            <AlertDialogDescription>
              Future digests will be sent from donotreply@elementarai.com.
              Your Outlook connection stays active — you can switch back anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onClick={() => applyMode("donotreply")}
              disabled={pending}
            >
              {pending ? "Switching..." : "Switch"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// ConnectedMailboxCard — replaces the old "Sending identity" card
// ---------------------------------------------------------------------------

function ConnectedMailboxCard({ refreshKey, onChanged }) {
  const [oauthStatus, setOauthStatus] = useState(null); // null = loading
  const [oauthError, setOauthError] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = useCallback(async () => {
    setOauthError(false);
    try {
      const res = await api.get("/email/oauth/status");
      setOauthStatus(res.data);
    } catch {
      setOauthError(true);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, refreshKey]);

  async function handleConnect() {
    try {
      const res = await api.get("/email/oauth/authorize");
      window.location.href = res.data.authorize_url;
    } catch {
      toast.error("Failed to start the Outlook connection. Please try again.");
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await api.post("/email/oauth/disconnect");
      toast.success("Outlook disconnected.");
      setDisconnectDialogOpen(false);
      loadStatus();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to disconnect. Please try again."));
    } finally {
      setDisconnecting(false);
    }
  }

  const status = oauthStatus?.status;

  function renderContent() {
    if (oauthError) {
      return <p className="text-sm text-red-600">Unable to load Outlook connection status.</p>;
    }
    if (oauthStatus === null) {
      return <p className="text-sm text-gray-400">Loading...</p>;
    }

    if (status === "connected") {
      return (
        <div className="space-y-3">
          <div className="flex gap-2 text-sm">
            <span className="text-gray-500 w-40">Connected mailbox:</span>
            <span className="font-medium font-mono text-xs">{oauthStatus.connected_mailbox}</span>
          </div>
          {oauthStatus.consent_at && (
            <div className="flex gap-2 text-sm">
              <span className="text-gray-500 w-40">Last consent:</span>
              <span className="text-gray-700">{formatConsentDate(oauthStatus.consent_at)}</span>
            </div>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDisconnectDialogOpen(true)}
          >
            Disconnect
          </Button>
        </div>
      );
    }

    if (status === "error" || status === "expired") {
      const title = status === "expired" ? "Outlook connection expired" : "Outlook connection error";
      const body = oauthStatus.last_error || "Reconnect required";
      return (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <p className="text-sm font-medium text-amber-800">{title}</p>
            <p className="text-xs text-amber-700 mt-0.5">{body}</p>
          </div>
          <Button
            size="sm"
            className="text-white"
            style={{ backgroundColor: BRAND.bg }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
            onClick={handleConnect}
          >
            Reconnect
          </Button>
        </div>
      );
    }

    // not_connected (default)
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Connect your organization&apos;s shared mailbox to send documents. Recipients will see mail
          from your real address.
        </p>
        <Button
          size="sm"
          className="text-white"
          style={{ backgroundColor: BRAND.bg }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
          onClick={handleConnect}
        >
          Connect Outlook
        </Button>
      </div>
    );
  }

  function renderTitle() {
    if (status === "connected") return `Connected: ${oauthStatus.connected_mailbox}`;
    if (status === "error") return "Outlook connection error";
    if (status === "expired") return "Outlook connection expired";
    return "Outlook is not connected";
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle data-testid="mailbox-card-title">
            {oauthStatus === null && !oauthError ? "Outlook mailbox" : renderTitle()}
          </CardTitle>
          <CardDescription>The mailbox your documents are sent from.</CardDescription>
        </CardHeader>
        <CardContent>{renderContent()}</CardContent>
      </Card>

      {/* Disconnect confirmation */}
      <AlertDialog
        open={disconnectDialogOpen}
        onOpenChange={(o) => !o && setDisconnectDialogOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Outlook?</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop email sending for the tenant until reconnected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? "Disconnecting..." : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// GroupSelect — combobox-like control for the group field
// ---------------------------------------------------------------------------

function GroupSelect({ value, onChange, groups }) {
  const [open, setOpen] = useState(false);
  const [showNewInput, setShowNewInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  // value: null | string

  const groupNames = groups.map((g) => g.group_name);

  function handleSelect(name) {
    onChange(name);
    setOpen(false);
    setShowNewInput(false);
    setNewGroupName("");
  }

  function handleClear() {
    onChange(null);
    setOpen(false);
    setShowNewInput(false);
    setNewGroupName("");
  }

  function handleNewGroupConfirm() {
    if (newGroupName.trim()) {
      onChange(newGroupName.trim().toLowerCase());
    }
    setOpen(false);
    setShowNewInput(false);
    setNewGroupName("");
  }

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="group-select-trigger"
        className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus:outline-none"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || "No group"}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg"
          role="listbox"
        >
          {/* Existing groups */}
          {groupNames.map((name) => (
            <button
              key={name}
              type="button"
              role="option"
              aria-selected={value === name}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              onClick={() => handleSelect(name)}
            >
              {name}
            </button>
          ))}

          {/* Clear group */}
          <button
            type="button"
            role="option"
            data-testid="group-select-clear"
            className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-t"
            onClick={handleClear}
          >
            Clear group
          </button>

          {/* Create new group */}
          {!showNewInput ? (
            <button
              type="button"
              data-testid="group-select-new"
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border-t"
              onClick={() => setShowNewInput(true)}
            >
              + Create new group
            </button>
          ) : (
            <div className="px-3 py-2 border-t space-y-2">
              <Input
                autoFocus
                data-testid="new-group-input"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g. tax"
                className="text-sm h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleNewGroupConfirm();
                  }
                  if (e.key === "Escape") {
                    setShowNewInput(false);
                    setNewGroupName("");
                  }
                }}
              />
              {newGroupName && !isValidGroupName(newGroupName) && (
                <p className="text-xs text-red-500">
                  Use letters, numbers, spaces, hyphens or underscores (2-64 chars).
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="h-7 text-xs text-white"
                  style={{ backgroundColor: BRAND.bg }}
                  disabled={!newGroupName.trim() || !isValidGroupName(newGroupName)}
                  onClick={handleNewGroupConfirm}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => {
                    setShowNewInput(false);
                    setNewGroupName("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact dialog (create / edit) — now includes group field
// ---------------------------------------------------------------------------

function ContactDialog({ open, onOpenChange, contact, groups, onSaved }) {
  const isEdit = !!contact;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [groupName, setGroupName] = useState(null); // null = no group
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(contact?.name ?? "");
      setEmail(contact?.email ?? "");
      setGroupName(contact?.group_name ?? null);
      setErrors({});
    }
  }, [open, contact]);

  function validate() {
    const errs = {};
    if (!email.trim()) errs.email = "Email is required.";
    else if (!isValidEmail(email.trim())) errs.email = "Enter a valid email address.";
    if (name.length > 120) errs.name = "Name must be 120 characters or fewer.";
    if (groupName !== null && !isValidGroupName(groupName)) {
      errs.groupName = "Use letters, numbers, spaces, hyphens or underscores (2-64 chars).";
    }
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      if (isEdit) {
        // Build body: include group_name explicitly (null to clear, string to set, omit to leave)
        const body = {
          email: email.trim(),
          name: name.trim() || undefined,
          group_name: groupName, // null clears, string sets
        };
        // If unchanged from original, omit the field entirely
        if (groupName === (contact.group_name ?? null)) {
          delete body.group_name;
        }
        await api.put(`/email/contacts/${contact.id}`, body);
        toast.success("Contact updated.");
      } else {
        const body = {
          email: email.trim(),
          name: name.trim() || undefined,
        };
        if (groupName) {
          body.group_name = groupName;
        }
        await api.post("/email/contacts", body);
        toast.success("Contact added.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, "Email already exists"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit contact" : "Add contact"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this contact's details." : "Add a new contact to your address book."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="contact-name">Name (optional)</Label>
            <Input
              id="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alice Smith"
              maxLength={120}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="contact-email">Email *</Label>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@example.com"
            />
            {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
          </div>
          <div className="space-y-1">
            <Label>Group (optional)</Label>
            <GroupSelect
              value={groupName}
              onChange={setGroupName}
              groups={groups}
            />
            {errors.groupName && <p className="text-xs text-red-500">{errors.groupName}</p>}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onMouseEnter={(e) =>
                !submitting && (e.currentTarget.style.backgroundColor = BRAND.hover)
              }
              onMouseLeave={(e) =>
                !submitting && (e.currentTarget.style.backgroundColor = BRAND.bg)
              }
            >
              {submitting ? "Saving..." : isEdit ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------

export default function EmailContactsTab() {
  // Shared OAuth-status refresh key — bumped by ConnectedMailboxCard +
  // SenderModeCard on disconnect / mode change so both re-render.
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  // Groups (derived from contacts — for the filter dropdown + contact dialog)
  const [groups, setGroups] = useState([]);

  // Group filter selection
  const [groupFilter, setGroupFilter] = useState(""); // "" = All

  // Contact dialog state
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Delete contact confirm
  const [deletingContact, setDeletingContact] = useState(null);

  // ---------------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------------

  const loadGroups = useCallback(async () => {
    // Phase A 2026-04-27: source the Group dropdown from the LP groups
    // (manually-created groups via /api/groups) so the contact's group_name
    // references real groups carrying scraper-native member-name lists.
    // The legacy /api/email/groups (distinct free-text tags) is no longer
    // the picker source; orphan tag-based contacts still display via the
    // contacts table itself, but new contacts attach to LP groups.
    try {
      const res = await api.get("/groups");
      const lpGroups = Array.isArray(res.data?.groups) ? res.data.groups : [];
      // Shape adapter: map {name, members, ...} → {group_name, member_count}
      // so existing GroupSelect props keep working.
      setGroups(
        lpGroups.map((g) => ({
          group_name: g.name,
          member_count: Array.isArray(g.members) ? g.members.length : 0,
        }))
      );
    } catch {
      setGroups([]);
    }
  }, []);

  const loadContacts = useCallback(
    async (filter) => {
      setContactsLoading(true);
      try {
        const url = filter ? `/email/contacts?group_name=${encodeURIComponent(filter)}` : "/email/contacts";
        const res = await api.get(url);
        setContacts(Array.isArray(res.data) ? res.data : []);
      } catch {
        setContacts([]);
      } finally {
        setContactsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadGroups();
    loadContacts("");
  }, [loadGroups, loadContacts]);

  // Re-fetch contacts when filter changes
  function handleFilterChange(value) {
    setGroupFilter(value);
    loadContacts(value);
  }

  // After contact save: refresh both contacts (with current filter) and groups
  function handleContactSaved() {
    loadContacts(groupFilter);
    loadGroups();
  }

  // ---------------------------------------------------------------------------
  // Contact actions
  // ---------------------------------------------------------------------------

  async function handleDeleteContact(contact) {
    try {
      await api.delete(`/email/contacts/${contact.id}`);
      toast.success("Contact deleted.");
      loadContacts(groupFilter);
      loadGroups();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete contact."));
    } finally {
      setDeletingContact(null);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* Card 1: Connected mailbox (OAuth)                                   */}
      {/* ------------------------------------------------------------------ */}
      <SenderModeCard
        refreshKey={statusRefreshKey}
        onChanged={() => setStatusRefreshKey((k) => k + 1)}
      />

      <ConnectedMailboxCard
        refreshKey={statusRefreshKey}
        onChanged={() => setStatusRefreshKey((k) => k + 1)}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Card 2: Address book                                               */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Address book</CardTitle>
            <CardDescription>Manage your email contacts and groups.</CardDescription>
          </div>
          <Button
            size="sm"
            className="text-white"
            style={{ backgroundColor: BRAND.bg }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
            onClick={() => {
              setEditingContact(null);
              setContactDialogOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add contact
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Group filter */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-gray-500 whitespace-nowrap">Filter by group:</Label>
            <select
              className="text-sm border rounded-md px-2 py-1 bg-white"
              value={groupFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
              aria-label="Filter by group"
            >
              <option value="">All</option>
              {groups.map((g) => (
                <option key={g.group_name} value={g.group_name}>
                  {g.group_name} ({g.member_count})
                </option>
              ))}
            </select>
          </div>

          {/* Table */}
          {contactsLoading ? (
            <p className="text-sm text-gray-400">Loading contacts...</p>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-gray-500">
              {groupFilter ? "No contacts in this group." : "No contacts yet. Add one to get started."}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm">{c.name || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell className="text-sm font-mono text-xs">{c.email}</TableCell>
                    <TableCell className="text-sm">
                      {c.group_name ? (
                        <span
                          className="inline-block text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: BRAND.soft, color: BRAND.text }}
                        >
                          {c.group_name}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit contact"
                          onClick={() => {
                            setEditingContact(c);
                            setContactDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete contact"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => setDeletingContact(c)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Dialogs                                                             */}
      {/* ------------------------------------------------------------------ */}

      {/* Contact create/edit */}
      <ContactDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        contact={editingContact}
        groups={groups}
        onSaved={handleContactSaved}
      />

      {/* Contact delete confirm */}
      <AlertDialog
        open={!!deletingContact}
        onOpenChange={(o) => !o && setDeletingContact(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingContact
                ? `This will permanently remove ${deletingContact.name || deletingContact.email} from your contacts.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleDeleteContact(deletingContact)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
